import { SEO_CONFIG } from '@/config/seo.config'

/**
 * Serialize a JSON-LD block for a <script type="application/ld+json"> tag. Escaping `<`/`>`/`&`
 * to unicode escapes keeps the payload valid JSON while making a </script> breakout impossible
 * (same treatment as the content-pages serializer).
 */
function serializeJsonLd(block: Record<string, unknown>): string {
    return JSON.stringify(block).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
}

/**
 * FAQPage schema for a rendered FAQ section, so the questions/answers are eligible for rich
 * results. Emit at most ONE per page (Google ignores multiple FAQPage blocks). Only plain-string
 * answers are included — rich-node answers are skipped rather than serialized as "[object Object]".
 */
export function FaqStructuredData({ items }: { items: ReadonlyArray<{ question: string; answer: React.ReactNode }> }) {
    const faqs = items.filter((item): item is { question: string; answer: string } => typeof item.answer === 'string')
    if (faqs.length === 0) return null

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
    }

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(structuredData) }} />
}

export function OrganizationStructuredData() {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SEO_CONFIG.siteName,
        url: SEO_CONFIG.siteUrl,
        logo: `${SEO_CONFIG.siteUrl}/logo.png`,
        description: SEO_CONFIG.description,
        sameAs: ['https://twitter.com/superscale', 'https://www.linkedin.com/company/superscale'],
    }

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
}

export function WebPageStructuredData({
    title,
    description,
    url,
}: {
    title: string
    description: string
    url: string
}) {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description: description,
        url: url,
        publisher: {
            '@type': 'Organization',
            name: SEO_CONFIG.siteName,
            url: SEO_CONFIG.siteUrl,
        },
    }

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
}

export function BreadcrumbStructuredData({ items }: { items: Array<{ name: string; url: string }> }) {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
        })),
    }

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
}

export function SoftwareApplicationStructuredData() {
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: SEO_CONFIG.siteName,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
        },
        description: SEO_CONFIG.description,
        url: SEO_CONFIG.siteUrl,
    }

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
}
